import { toast } from '@heroui/react'
import { create } from 'zustand'

import { driversApi } from '@/api/drivers.api'
import { reportsTimeApi } from '@/api/reportsTime.api'
import i18n from '@/i18n'
import type { Pagination, UUID } from '@/types/common'
import type { DriverExpense } from '@/types/driver'
import type { TimeReport } from '@/types/timeReport'

interface TimesheetFilters {
  reportId?: string;
  year: number;
  month: number;
  driverID?: UUID;
  activityType?: string; // Optional local filter for activity types
}

interface TimesheetState {
  reportId?: string; // Currently loaded report ID
  report?: TimeReport; // Currently loaded report
  expenses: DriverExpense[];
  pagination: Pagination;
  filters: TimesheetFilters;
  isLoading: boolean;
  selectedDay: { driverId?: UUID; date: Date } | null;

  setReportId: (id: string | undefined) => void;
  fetchData: () => Promise<void>;
  setFilters: (filters: Partial<TimesheetFilters>) => void;
  setSelectedDay: (day: { driverId?: UUID; date: Date } | null) => void;
  addActivity: (item: any) => void;
  removeActivity: (index: number) => void;
  updateActivity: (index: number, item: any) => void;
}

const currentDate = new Date()

export const useTimesheetStore = create<TimesheetState>((set, get) => ({
  reportId: undefined,
  report: undefined,
  expenses: [],
  pagination: { Total: 0, Limit: 100, Page: 0, TotalPages: 0, HasNext: false, HasPrev: false },
  filters: {
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
  },
  isLoading: false,
  selectedDay: null,

  setReportId: id => {
    set({ reportId: id })
    get().fetchData()
  },

  fetchData: async () => {
    const { filters, reportId } = get()
    set({ isLoading: true })
    try {
      let expensesRes: { Expenses?: DriverExpense[], Pagination?: Pagination } = {}
      let fetchedReport: TimeReport | undefined = undefined
      
      const promises: Promise<any>[] = []

      // If driver is selected, always fetch expenses for the calendar
      if (filters.driverID) {
        const dateStr = `${filters.year}-${String(filters.month).padStart(2, '0')}`
        promises.push(
          driversApi.getDriverExpenses(filters.driverID, { date: dateStr, limit: 100 })
            .then(res => { expensesRes = res as any })
        )
      }

      // If we have a specific report ID (edit mode), fetch it by ID
      if (reportId) {
        promises.push(
          reportsTimeApi.getTimeReport(reportId).then(res => { fetchedReport = res })
        )
      }

      await Promise.all(promises)

      set({
        report: fetchedReport, 
        expenses: expensesRes.Expenses || [],
        pagination: expensesRes.Pagination || { Total: 0, Limit: 100, Page: 0, TotalPages: 0, HasNext: false, HasPrev: false },
        isLoading: false
      })
    } catch (error) {
      console.error('Failed to fetch data', error)
      const message = (error as Error).message
      toast.danger(i18n.t('timesheet.toast.fetch_error'), { description: message })
      set({ report: undefined, expenses: [], isLoading: false })
    }
  },

  setFilters: newFilters => {
    const state = get()
    const hasChanged = Object.keys(newFilters).some(
      key => newFilters[key as keyof TimesheetFilters] !== state.filters[key as keyof TimesheetFilters]
    )

    if (!hasChanged) return // Do nothing if filters haven't changed

    set(state => ({
      filters: { ...state.filters, ...newFilters },
      selectedDay: null, // Reset selection when filters change
    }))
    get().fetchData()
  },

  setSelectedDay: day => {
    set({ selectedDay: day })
  },

  addActivity: item => {
    set(state => {
      const currentReport = state.report || { Items: [] } as any
      return {
        report: {
          ...currentReport,
          Items: [...(currentReport.Items || []), item]
        }
      }
    })
  },

  removeActivity: indexToRemove => {
    set(state => {
      if (!state.report?.Items) return state
      
      const newItems = [...state.report.Items]
      newItems.splice(indexToRemove, 1)
      
      return {
        report: {
          ...state.report,
          Items: newItems
        }
      }
    })
  },

  updateActivity: (index, item) => {
    set(state => {
      if (!state.report?.Items) return state
      
      const newItems = [...state.report.Items]
      newItems[index] = { ...newItems[index], ...item }
      
      return {
        report: {
          ...state.report,
          Items: newItems
        }
      }
    })
  }
}))
